<template>
  <BaseCard
    title="New Businesses By Month"
    class="h-full"
  >
    <template #content>
      <div class="flex flex-col gap-4 min-h-fit w-full">
        <div class="flex flex-col gap-4 w-full">
          <div v-if="isLoading">
            <Skeleton
              height="400px"
              width="100%"
            />
          </div>
          <div
            v-else-if="newBusinessesByMonth.length > 0"
            class="flex flex-col gap-4 w-full  rounded-lg"
          >
            <ClientOnly>
              <VChart

                :option="option"
                autoresize
              />
              <template #fallback>
                <div class="text-center text-gray-500 p-4">
                  Loading chart...
                </div>
              </template>
            </ClientOnly>
          </div>

          <div
            v-else
            class="flex flex-col items-center justify-center p-12 rounded-lg"
          >
            <Icon
              name="mdi:chart-line"
              size="48"
              class="text-gray-300 mb-4"
            />
            <p class="text-gray-500 text-center">
              No new businesses data available
            </p>
          </div>
        </div>
      </div>
    </template>
  </BaseCard>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { use } from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers'
import { BarChart } from 'echarts/charts'
import {
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent,
} from 'echarts/components'
import VChart from 'vue-echarts'
import type { ComposeOption } from 'echarts/core'
import type {
  BarSeriesOption,
  TitleComponentOption,
  TooltipComponentOption,
  LegendComponentOption,
  GridComponentOption,
} from 'echarts'
import { useDashboard } from '~/composables/useDashboardCA'

use([
  CanvasRenderer,
  BarChart,
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent,
])

type EChartsOption = ComposeOption<
  | BarSeriesOption
  | TitleComponentOption
  | TooltipComponentOption
  | LegendComponentOption
  | GridComponentOption
>

interface MonthlyData {
  month: string
  monthLabel: string
  count: number
}

interface BusinessAnalyticsResponse {
  pipelineStatus: unknown[]
  documentStatus: unknown
  newBusinessesByMonth: MonthlyData[]
  soldBusinessesByMonth: unknown[]
  businessesByAgent: unknown[]
}

const { getBusinessAnalytics } = useDashboard()

const isLoading = ref(true)
const newBusinessesByMonth = ref<MonthlyData[]>([])

const getPrimaryColor = () => {
  return getComputedStyle(document.documentElement)
    ?.getPropertyValue('--p-primary-200')
    ?.trim() || '#97BA9F'
}

const option = computed<EChartsOption>(() => {
  if (newBusinessesByMonth.value.length === 0) {
    return {
      title: {
        text: 'No Data',
        left: 'center',
        top: 'middle',
        textStyle: {
          color: '#999',
        },
      },
    }
  }

  const primaryColor = getPrimaryColor()
  const labels = newBusinessesByMonth.value.map(item => item.monthLabel)
  const data = newBusinessesByMonth.value.map(item => item.count)

  return {
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow',
      },
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      borderColor: 'transparent',
      textStyle: {
        color: '#fff',
      },
      formatter: (params: unknown) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const param = Array.isArray(params) ? params[0] : params as any
        const value = param?.value
        if (value === null || value === undefined) return ''
        return `${param.name}<br/>${value} businesses`
      },
    },
    grid: {
      left: '10%',
      right: '5%',
      bottom: '25%',
      top: '5%',
      containLabel: false,
    },
    xAxis: {
      type: 'category',
      data: labels,
      axisLabel: {
        interval: 0,
        fontSize: 10,
        color: '#666',
        rotate: 45,
      },
      axisLine: {
        show: true,
        lineStyle: {
          color: '#e0e0e0',
        },
      },
      axisTick: {
        show: false,
      },
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        color: '#666',
      },
      splitLine: {
        lineStyle: {
          color: 'rgba(0, 0, 0, 0.05)',
        },
      },
    },
    series: [
      {
        name: 'New Businesses',
        type: 'bar',
        data,
        barWidth: '80%',
        itemStyle: {
          color: primaryColor,
          borderRadius: [4, 4, 0, 0],
        },
        label: {
          show: true,
          position: 'top',
          fontSize: 10,
          color: '#333',
        },
        emphasis: {
          itemStyle: {
            shadowBlur: 0,
            shadowOffsetX: 0,
            shadowColor: 'transparent',
          },
        },
      },
    ],
    animation: true,
    animationDuration: 1000,
    animationEasing: 'cubicOut',
  }
})

const getData = async () => {
  isLoading.value = true
  try {
    const response = await getBusinessAnalytics()
    const data = response?.data as BusinessAnalyticsResponse

    if (data) {
      newBusinessesByMonth.value = data.newBusinessesByMonth || []
    }
  }
  catch (error) {
    console.error('Error fetching business analytics:', error)
    newBusinessesByMonth.value = []
  }
  finally {
    isLoading.value = false
  }
}

onMounted(async () => {
  await getData()
})
</script>
